'use client';

import { usePathname, useRouter } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import Image from 'next/image';
import { Globe2Icon, X } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getRoute } from '@/lib/navigation';
import { Locale, languages } from '@/constants/locales';
import { Routes } from '@/constants/routes';

type LanguageSwitcherProps = {
    variant?: 'default' | 'light' | 'button';
    inFooter?: boolean;
    inMobileMenu?: boolean;
    onLanguageChange?: () => void;
};

export function LanguageSwitcher({
    variant = 'default',
    inFooter = false,
    inMobileMenu = false,
    onLanguageChange
}: LanguageSwitcherProps) {
    const pathname = usePathname();
    const router = useRouter();
    const currentLocale = useLocale() as Locale;
    const t = useTranslations('header.languageSwitcher');
    const [isOpen, setIsOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [mounted, setMounted] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [dropdownDirection, setDropdownDirection] = useState<'down' | 'up'>('down');
    const [horizontalDirection, setHorizontalDirection] = useState<'left' | 'right'>('left');

    // Check if component is mounted (for SSR safety)
    useEffect(() => {
        setMounted(true);
    }, []);

    // Check if we're on mobile
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (isMobile && isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isMobile, isOpen]);

    useEffect(() => {
        if (!isOpen || isMobile) return;

        // For mobile menu, always position dropdown down and to the left
        if (inMobileMenu) {
            setDropdownDirection('down');
            setHorizontalDirection('left');
            return;
        }

        if (typeof window !== 'undefined' && window.innerWidth < 768) {
            setDropdownDirection('down');
            setHorizontalDirection('right');
            return;
        }
        if (inFooter) {
            setDropdownDirection('up');
            setHorizontalDirection('right');
            return;
        }
        const button = dropdownRef.current;
        if (button) {
            const rect = button.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            const spaceLeft = rect.left;
            const spaceRight = window.innerWidth - rect.right;
            setDropdownDirection(spaceBelow < 250 && spaceAbove > spaceBelow ? 'up' : 'down');
            setHorizontalDirection(spaceLeft < 250 && spaceRight > spaceLeft ? 'right' : 'left');
        }
    }, [isOpen, inFooter, inMobileMenu, isMobile]);

    const handleLanguageChange = (locale: string) => {
        const [, currentRoute] = window.location.pathname === '/' ? ['en', Routes.home] : getRoute(pathname, currentLocale);

        if (!currentRoute) {
            router.push(`/${locale}`);
        } else {
            const newPathname = currentRoute.href[locale as keyof typeof currentRoute.href];
            router.push(newPathname);
        }

        setIsOpen(false);
        onLanguageChange?.();
    };

    const currentLanguage = languages.find(lang => lang.code === currentLocale);

    const buttonClasses = variant === 'light'
        ? "flex items-center space-x-2 text-white hover:text-blue-200 transition-colors cursor-pointer"
        : variant === 'button'
            ? "relative px-4 py-2 text-sm font-bold rounded-xl transition-all duration-300 flex items-center gap-2 text-white/80 hover:text-white hover:bg-black/90 cursor-pointer !outline-none"
            : "flex items-center space-x-2 text-gray-600 hover:text-blue-600 transition-colors cursor-pointer";

    // Special styling for mobile menu
    const mobileMenuButtonClasses = inMobileMenu
        ? "w-full max-w-xs px-6 py-4 text-sm font-bold rounded-xl transition-all duration-200 flex items-center justify-between text-white/80 hover:text-white bg-black/80 hover:bg-black/90 cursor-pointer"
        : buttonClasses;

    // Adjust dropdown z-index and positioning for mobile menu
    const dropdownClasses = inMobileMenu && !isMobile
        ? `absolute left-0 right-0 mt-2 bg-white rounded-md shadow-lg py-1 z-[60] mx-6`
        : `absolute ${horizontalDirection === 'left' ? 'right-0' : 'left-0'} ${dropdownDirection === 'up' ? 'bottom-full mb-2' : 'mt-2'} w-48 bg-white rounded-md shadow-lg py-1 z-50`;

    const renderLanguageOption = (language: typeof languages[0]) => (
        <button
            key={language.code}
            onClick={() => handleLanguageChange(language.code)}
            className={`w-full text-left px-4 py-3 text-base flex items-center space-x-3 cursor-pointer transition-colors ${currentLocale === language.code
                ? 'text-blue-600 bg-blue-50'
                : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50'
                }`}
        >
            <div className="relative flex-shrink-0">
                <Image
                    src={language.flag}
                    alt={`${language.name} flag`}
                    width={24}
                    height={24}
                    className="rounded-sm"
                />
            </div>
            <span className="font-medium">{language.name}</span>
            {currentLocale === language.code && (
                <span className="ml-auto text-blue-600">✓</span>
            )}
        </button>
    );

    const renderMobileModal = () => (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/50 z-[9998]"
                onClick={() => setIsOpen(false)}
            />

            {/* Modal */}
            <div className="fixed inset-x-0 bottom-0 z-[9999] bg-white rounded-t-2xl shadow-2xl">
                {/* Modal Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">
                        {t('selectLanguage')}
                    </h3>
                    <button
                        onClick={() => setIsOpen(false)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        aria-label={t('closeModal')}
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Language Options */}
                <div className="max-h-[60vh] overflow-y-auto p-4">
                    <div className="space-y-1">
                        {languages.map(renderLanguageOption)}
                    </div>
                </div>

                {/* Safe area for iPhone home indicator */}
                <div className="h-safe-area-inset-bottom" />
            </div>
        </>
    );

    return (
        <div className={inMobileMenu ? "relative w-full" : "relative"} ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={inMobileMenu ? mobileMenuButtonClasses : buttonClasses}
                aria-label="Select language"
            >
                <div className="flex items-center space-x-2">
                    {currentLanguage?.flag && (
                        <Image
                            src={currentLanguage.flag}
                            alt={`${currentLanguage.name} flag`}
                            width={20}
                            height={20}
                            className="rounded-sm"
                        />
                    )}
                    <span>{currentLanguage?.name}</span>
                </div>
                <svg
                    className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                    />
                </svg>
            </button>

            {isOpen && (
                <>
                    {isMobile && mounted ? (
                        // Mobile Modal using Portal
                        createPortal(renderMobileModal(), document.body)
                    ) : (
                        // Desktop Dropdown
                        <div className={dropdownClasses}>
                            {languages.map((language) => (
                                <button
                                    key={language.code}
                                    onClick={() => handleLanguageChange(language.code)}
                                    className={`w-full text-left px-4 py-2 text-sm flex items-center space-x-2 cursor-pointer ${currentLocale === language.code
                                        ? 'text-blue-600 bg-blue-50'
                                        : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50'
                                        }`}
                                >
                                    <div className="relative">
                                        <Image
                                            src={language.flag}
                                            alt={`${language.name} flag`}
                                            width={20}
                                            height={20}
                                        />
                                    </div>
                                    <span>{language.name}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
} 